import {
  type IPlatform,
  type IThirdPartyPlatform,
  type IThirdPartyProvider,
  PLATFORMS,
  THIRD_PARTY_PLATFORMS,
  THIRD_PARTY_PROVIDERS,
  UnsupportedPlatform,
  PLATFORM_CATEGORIES,
  PLATFORMS_CATEGORIES_ORDER,
  type PlatformCategory
} from 'src/dataSource/constants'
import { type ClientAccountsResp, PlatformMapping, type ExtendedPlatforms, type Platforms } from 'shared/api/accounts'
import { type ExternalAccount } from 'shared/api/credentials'
import { type IAccountsList } from './components/Popup/AccountSelectionPopup'
import { clone } from 'ramda'
import { type Client } from 'shared/context/ClientContext'
import {
  THIRD_PARTY_PLATFORM,
  type ThirdPartyPlatforms,
  type FivetranPlatform,
  FIVETRAN_PLATFORM,
  THIRD_PARTY_PROVIDER,
  type ThirdPartyProviders
} from 'shared/thirdParty'
import { PLATFORM, SUPPORTED_PLATFORMS, type SupportedPlatforms } from 'src/dataSource/types'
import { isSupermetricsPlatform } from 'src/dataSource/platformGuards'
import { type FeaturePlan } from 'shared/api/clients'
import toSnakeCase from 'snakecase-keys'

export { isSupermetricsPlatform }

export const getPlatformDetailsByLabel = (label: string): IPlatform => {
  return PLATFORMS.find((dataSource) => dataSource.label === label) as IPlatform
}

export const getPlatformDetailsByPlatform = (
  platform: string | undefined,
  unsupportedOnNotFound = false
): IPlatform => {
  if (platform == null) return undefined as unknown as IPlatform

  if (platform in PlatformMapping) platform = PlatformMapping[platform as ExtendedPlatforms]

  const platformDetails = PLATFORMS.find((dataSource) => dataSource.platform === platform)

  if (platformDetails != null) return platformDetails

  if (unsupportedOnNotFound) return UnsupportedPlatform(platform)

  return undefined as unknown as IPlatform
}

export const getThirdPartyProviderDetailsByName = (name: string | null | undefined): IThirdPartyProvider | null => {
  return THIRD_PARTY_PROVIDERS.find(({ platform }) => platform === name) ?? null
}

export const isPartOfGooglePlatform = (platform: string): boolean => {
  return (
    platform === 'dv360' ||
    platform === 'analytics' ||
    platform === 'ga4' ||
    platform === 'gcp' ||
    platform === 'google' ||
    platform === 'merchant_center' ||
    platform === 'google_sheets' ||
    platform === 'google_search_console'
  )
}

export const isThirdPartyPlatform = (platform: string): platform is ThirdPartyPlatforms => {
  const thirdPartyPlatforms = Object.values<string>(THIRD_PARTY_PLATFORM)

  return thirdPartyPlatforms.includes(platform)
}

export const isFivetranPlatform = (platform: ThirdPartyPlatforms): platform is FivetranPlatform => {
  const fivetranPlatforms = Object.values<string>(FIVETRAN_PLATFORM)
  return fivetranPlatforms.includes(platform)
}

export const getResponseKeyFromUrl = (url: string, key: string): string | null => {
  const urlObject = new URL(url)
  return urlObject.searchParams.get(key)
}

export const isSupportedCredentialPlatform = (
  platform: Platforms | ThirdPartyProviders
): platform is SupportedPlatforms => {
  return SUPPORTED_PLATFORMS.includes(platform as SupportedPlatforms)
}

export const mapToAccounts = (
  accounts: ExternalAccount[] | ClientAccountsResp[],
  currentClient: Client,
  platform: Platforms
): IAccountsList[] => {
  return accounts.map((account) => {
    const mappedAccount = clone(account) as IAccountsList
    mappedAccount.selected = false
    mappedAccount.clientId = currentClient.id
    mappedAccount.platform = platform
    mappedAccount.id = account.id.toString()
    return mappedAccount
  })
}

/**
 * Remove any HTML tags, attributes and event handlers or inline script in the string
 */
export const sanitizePostMessageContent = <T extends string = string>(
  message: string | undefined
): T | '' | undefined => {
  return message?.replace(/<[^>]{0,10000}>/g, '')?.replace(/on[a-zA-Z]{1,50}="[^"]{0,10000}"/g, '') as T
}

/**
 * This function checks whether the supplied lowercase searchQuery matches account.DisplayName or account.name or account-id
 */
export const SearchQueryMatchesCriteria =
  (searchValueLowerCase: string) =>
  (account: IAccountsList): boolean => {
    const accountName = account.displayName ?? account.name
    const isNameMatch = Boolean(accountName?.toLowerCase()?.includes(searchValueLowerCase))
    const isRegionIdMatch = Boolean(account.regionId?.toLowerCase()?.includes(searchValueLowerCase))
    const isCountryCodeMatch = Boolean(account.countryCode?.toLowerCase()?.includes(searchValueLowerCase))
    const isIdMatch = Boolean(account.id.toLowerCase().includes(searchValueLowerCase))
    return isNameMatch || isIdMatch || isRegionIdMatch || isCountryCodeMatch
  }

export const areAllExternalAccountsConnected = (
  dataSources: IAccountsList[],
  externalAccounts: ExternalAccount[]
): boolean => {
  if (dataSources.length !== externalAccounts.length) {
    return false
  }
  const externalAccountIds = new Set(externalAccounts.map((account) => account.id))

  return dataSources.every((dataSource) => externalAccountIds.has(dataSource.externalAccountId))
}

/**
 * Validates a GCP Project ID.
 *
 * @param {string} projectId - The GCP Project ID to be validated.
 * @returns {boolean} - Returns true if the Project ID is valid, false otherwise.
 *
 * @description
 * This function checks the validity of a GCP Project ID based on the following criteria:
 * 1. The length of the string must be between 6 and 30 characters.
 * 2. The string must start with a lowercase letter.
 * 3. The string must only contain lowercase letters, digits, and hyphens.
 * 4. The string must not end with a hyphen.
 */
export const isValidGcpProjectId = (projectId: string): boolean => {
  const minLength = 6
  const maxLength = 30
  const regex = /^[a-z][a-z0-9-]*[a-z0-9]$/

  return projectId.length >= minLength && projectId.length <= maxLength && regex.test(projectId)
}

/**
 * The platforms the connect dialog offers: native data source platforms that are
 * neither deprecated nor hidden from this user.
 *
 * Shared by `ConnectNewDataSourcePopup` and the copilot connect tools, so the
 * agent can never offer a platform the user has no button for.
 */
export const getConnectablePlatforms = (
  shouldShowInternalFeatures: boolean,
  chatgptEnabled = true,
  platforms: IPlatform[] = PLATFORMS
): IPlatform[] =>
  platforms.filter(
    ({ platform, thirdPartyProvider, isDataSource, isDeprecated, isInternalOnly }) =>
      thirdPartyProvider == null &&
      isDataSource !== false &&
      isDeprecated !== true &&
      (!isInternalOnly || shouldShowInternalFeatures) &&
      (platform !== PLATFORM.CHATGPT_ADS || chatgptEnabled)
  )

/**
 * Whether this workspace may use a third-party platform: its feature plan has to
 * enable the platform, and everything but Supermetrics stays Precis-internal.
 *
 * Supermetrics platforms share a single `connectors.supermetrics` flag rather than
 * one flag each.
 */
export const hasThirdPartyPlatformAccess = (
  platform: ThirdPartyPlatforms,
  featurePlan: FeaturePlan | undefined,
  showInternalFeatures: boolean
): boolean => {
  const parsedFeaturePlan = featurePlan == null ? undefined : toSnakeCase(featurePlan, { deep: true })

  const isSupermetrics = isSupermetricsPlatform(platform)
  const parsedPlatform = isSupermetrics ? THIRD_PARTY_PROVIDER.SUPERMETRICS : platform

  const hasFeaturePlanAccess = parsedFeaturePlan?.connectors?.[parsedPlatform] === true

  return hasFeaturePlanAccess && (isSupermetrics || showInternalFeatures)
}

/**
 * The third-party platforms this workspace's feature plan allows as data sources.
 * Platforms carrying a `disabledMessage` are kept — the connect dialog renders
 * them disabled with that message, so filtering here would hide them entirely.
 */
export const getAccessibleThirdPartyPlatforms = (
  checkAccessToThirdPartyPlatform: (platform: ThirdPartyPlatforms) => boolean
): IThirdPartyPlatform[] =>
  THIRD_PARTY_PLATFORMS.filter(
    ({ platform, isDataSource }) => isDataSource !== false && checkAccessToThirdPartyPlatform(platform)
  )

/**
 * The third-party providers with at least one accessible platform behind them,
 * in the order the connect dialog lists them.
 */
export const getAccessibleThirdPartyProviders = (
  checkAccessToThirdPartyPlatform: (platform: ThirdPartyPlatforms) => boolean
): IThirdPartyProvider[] => {
  const accessibleProviders = new Set(
    getAccessibleThirdPartyPlatforms(checkAccessToThirdPartyPlatform).map(
      ({ thirdPartyProvider }) => thirdPartyProvider
    )
  )

  return THIRD_PARTY_PROVIDERS.filter((provider) => accessibleProviders.has(provider.platform))
}

/**
 * Whether a data source authenticates through the Alvie service account rather
 * than stored user credentials.
 */
export const isServiceAccountAuthenticatedDataSource = (dataSource: { credentialsId?: string | null }): boolean =>
  dataSource.credentialsId == null

/** Why the reconnect flow cannot be opened for a data source. */
export type DataSourceReconnectBlockerCode =
  | 'unknown_platform'
  | 'platform_deprecated'
  | 'reconnect_unsupported'
  | 'missing_third_party_credentials'

export interface DataSourceReconnectBlocker {
  code: DataSourceReconnectBlockerCode
  /** Platform metadata for the row, absent when the platform is unrecognized. */
  platform?: IPlatform
  /** Set for `missing_third_party_credentials` when the provider is known. */
  thirdPartyProviderLabel?: string
}

/**
 * The single reason the reconnect flow cannot be opened for a data source, or
 * null when reconnecting is allowed. Editor access is checked separately.
 *
 * Shared by the row menu and the copilot reconnect tools so both agree on what
 * is reconnectable — the row menu only disables on `platform_deprecated` and
 * `reconnect_unsupported`, the other codes dead-end further into the flow.
 */
export const getDataSourceReconnectBlocker = (dataSource: {
  platform: string
  credentialsId?: string | null
}): DataSourceReconnectBlocker | null => {
  const platform = getPlatformDetailsByPlatform(dataSource.platform)
  if (platform == null) {
    return { code: 'unknown_platform' }
  }

  if (platform.isDeprecated === true) {
    return { code: 'platform_deprecated', platform }
  }

  if (platform.supportsReconnect === false) {
    return { code: 'reconnect_unsupported', platform }
  }

  if (platform.thirdPartyProvider != null && dataSource.credentialsId == null) {
    const providerLabel = getThirdPartyProviderDetailsByName(platform.thirdPartyProvider)?.label

    return {
      code: 'missing_third_party_credentials',
      platform,
      ...(providerLabel != null && { thirdPartyProviderLabel: providerLabel })
    }
  }

  return null
}

export type PlatformsByCategory = Record<string, IPlatform[]>

export const groupPlatformsByCategory = <T extends { category?: PlatformCategory }>(
  platforms: T[]
): Record<string, T[]> => {
  return platforms.reduce<Record<string, T[]>>((acc, platform) => {
    const category = platform.category ?? PLATFORM_CATEGORIES.other
    acc[category] ??= []
    acc[category].push(platform)
    return acc
  }, {})
}

export const sortPlatformsByCategory = <T extends { label: string }>(
  platformsByCategory: Record<string, T[]>
): Array<[string, T[]]> => {
  const sortedCategories = Object.entries(platformsByCategory)
    .sort(([categoryA], [categoryB]) => {
      const indexA = PLATFORMS_CATEGORIES_ORDER.indexOf(categoryA as PlatformCategory)
      const indexB = PLATFORMS_CATEGORIES_ORDER.indexOf(categoryB as PlatformCategory)

      if (indexA === -1 && indexB === -1) {
        return categoryA.localeCompare(categoryB)
      }

      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })
    .map(([category, platforms]) => {
      const sortedPlatforms = [...platforms].sort((a, b) => a.label.localeCompare(b.label))
      return [category, sortedPlatforms] as [string, T[]]
    })

  return sortedCategories
}
